import { useMemo } from 'react'
import { Stack, Typography } from '@mui/material'
import { useGetProjectModels } from '@/service-library/hooks/project-models'
import { ProjectModelType } from '@/types/project-models'
import ProjectBreadcrumb from '@/components/project-tables/ProjectBreadcrumb'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import SettingsPage from '@/components/shared/settings/SettingsPage'
import ModelTable, { ModelGroup } from './ModelTable'

export default function ModelsTablePage() {
  const { project } = useProjectContext()

  // Fetch all project models for the current project
  const { projectModels, isPending } = useGetProjectModels({
    filters: {
      limit: '1000',
      project_id: project.id,
      fields__include: 'project_model_type,project_model_versions',
    },
  })

  // Group models by parent-child relationships
  const modelGroups: ModelGroup[] = useMemo(() => {
    if (!projectModels) return []

    const filteredModels = projectModels.filter((model) => {
      const modelType = (model.project_model_type as ProjectModelType).code
      return modelType !== 'OCR' && modelType !== 'AI Agent - OCR Review'
    })

    const parentModels = filteredModels.filter(
      (model) => !model.parent_model_id,
    )

    return parentModels.map((parent) => {
      const children = filteredModels.filter(
        (model) => model.parent_model_id === parent.id,
      )
      return { parentModel: parent, childModels: children }
    })
  }, [projectModels])

  return (
    <>
      <ProjectBreadcrumb label="Model Tables" />

      <SettingsPage
        title="Model Tables"
        description="The models in this project, with their accuracy and training status. Expand a row to see its versions."
        width="lg"
      >
        {isPending && (
          <Typography variant="body2" color="text.secondary">
            Loading models...
          </Typography>
        )}
        {!isPending && modelGroups.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No models found for this project.
          </Typography>
        )}
        {modelGroups.length > 0 && (
          <Stack spacing={3}>
            {modelGroups.map((group) => (
              <ModelTable key={group.parentModel.id} group={group} />
            ))}
          </Stack>
        )}
      </SettingsPage>
    </>
  )
}
